/**
 * @module support/shared/widgets
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */
/** */
/* tslint:disable:no-forward-ref */
/* tslint:disable:no-input-rename */
/* tslint:disable:no-output-rename */

import {
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  Output,
  Provider,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
/**
 * Provider Expression that allows tmo-checkbox to register as a ControlValueAccessor.
 * This allows it to support [(ngModel)].
 * @docs-private
 */
export const CHECKBOX_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => CheckboxComponent),
  multi: true,
};

/**
 * A T-mobile custom design checkbox component. Supports all of the functionality of an HTML5 checkbox,
 * and exposes a similar API. A tmo-checkbox can be either checked, unchecked or
 * disabled. Note that all additional accessibility attributes are taken care of by the component,
 * so there is no need to provide them yourself. However, if you want to omit a label and still
 * have the checkbox be accessible, you may supply an [aria-label] input.
 */
@Component({
  selector: 'tmo-checkbox',
  templateUrl: './checkbox.component.html',
  styleUrls: ['../../../../../../style-sheets/components/input-checkbox.component.scss'],
  providers: [CHECKBOX_CONTROL_VALUE_ACCESSOR],
})
export class CheckboxComponent implements ControlValueAccessor, OnDestroy {
  /**
   * Boolean to add margin top for label
   */
  @Input() public labelMarginTop?: boolean;

  /**
   * A unique id for each checkbox, if not supplied ID will not be added.
   * Best practice is to always provide an Id.
   */
  @Input() public id?: string;
  /**
   * Name input property to be assigned to each checkbox.
   * IMPORTANT : if it is used inside a <form>, name has to be unique
   */
  @Input() public name?: string;
  /**
   * Boolean input property to disable the checkbox.
   */
  @Input() public disabled?: boolean;
  /**
   * Boolean input property to make a checkbox mandatory, if used with a <form>
   */
  @Input() public required?: boolean;
  /**
   * NUmeric input property to add indexing order, to make check box accessible through keyboard
   */
  @Input() public tabindex?: number;
  /**
   * Boolean input property to set checkbox as Checked on initial load.
   * This property is optional, property provided to ngModel can also set checkbox as Checked.
   */
  @Input() public checked?: boolean;
  /**
   * Attached to the aria-label attribute of the host element. In most cases, arial-labelledby will
   * take precedence so this may be omitted.
   */
  @Input('aria-label') public ariaLabel?: string;
  /**
   * Users can specify the `aria-labelledby` attribute which will be forwarded to the input element
   */
  @Input('aria-labelledby') public ariaLabelledby?: string;

  @Input() public applyCheckboxMargin?: boolean;

  @Input() public teamAdjustment?: boolean;

  @Input() public teamFollowup?: boolean;

  @Input() public titleText?: string;

  @Input() public toolTip?: boolean;

  @Input() public checkboxTitle?: string;

  /** Event emitted when the checkbox's `checked` value changes.
   * It emits the 'Event' object.
   */
  @Output('onClick') public onCheckboxClick: EventEmitter<{}>;

  /** Internal property to store current state of checkbox. */
  private innerValue: boolean;

  constructor() {
    this.checked = false;
    this.disabled = false;
    this.required = false;
    const tabindex = 0;
    this.tabindex = tabindex;
    this.innerValue = false;
    this.ariaLabel = '';
    this.ariaLabelledby = null;
    this.onCheckboxClick = new EventEmitter<{}>();
  }

  /**
   * ngModel accessor to get the value of property (Boolean)
   * @docs-private
   */
  get value(): boolean {
    return this.innerValue || this.checked;
  }

  /**
   * ngModel accessor to set the value
   * @param value - Boolean value
   * @docs - private
   */
  set value(value: boolean) {
    if (value !== this.innerValue) {
      value = value === false ? undefined : value;
      this.innerValue = value || this.checked;
      this.onChangeCallback(value);
    }
  }

  /**
   * Called whenever a checkbox is focused out
   */
  public onBlur(): void {
    this.onTouchedCallback();
  }

  /**
   * Called whenever a checkbox is clicked
   * @param event - Event object
   * Emits an event with 'event' object as data.
   */
  public onClick(event: Event): void {
    this.onCheckboxClick.emit(event);
  }

  /**
   * Sets the model value. Implemented as part of ControlValueAccessor.
   * @param value Value to be set to the model.
   */
  public writeValue(value: boolean): void {
    if (value && value !== this.innerValue) {
      this.innerValue = value || this.checked;
      this.innerValue = this.innerValue === false ? undefined : this.innerValue;
      value = this.innerValue;
      this.onChangeCallback(value);
    }
  }

  /**
   * Registers a callback to be triggered when the value has changed.
   * Implemented as part of ControlValueAccessor.
   * @param fn Function to be called on change.
   */
  public registerOnChange(fn: (_: boolean) => {}): void {
    this.onChangeCallback = fn;
  }

  /**
   * Registers a callback to be triggered when the control has been touched.
   * Implemented as part of ControlValueAccessor.
   * @param fn Callback to be triggered when the checkbox is touched.
   * @returns void
   */
  public registerOnTouched(fn: () => {}): void {
    this.onTouchedCallback = fn;
  }

  /**
   * Destroys the subcriptions.
   * @returns void
   */
  public ngOnDestroy(): void {}

  /**
   * Called when the checkbox is blurred. Needed to properly implement ControlValueAccessor.
   * @docs-private
   * @returns void
   */
  private onTouchedCallback(): void {}

  /**
   * Called when the checkbox value is changed. Needed to properly implement ControlValueAccessor.
   * @docs-private
   * @returns void
   */
  private onChangeCallback(_: boolean): void {}
}
